import qs from 'querystring';
import {
  ApiRequestError,
  ApiTimeoutError,
  ApiNetworkError,
  ApiAbortError,
} from '@modules/Vector/F/Function/stickerizer/stickerizer.error';

export interface ApiCallOptions {
  headers?: Record<string, string>;
  timeoutMs?: number;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface FetchOptions {
  shouldAbortPrevious?: boolean;
}

export class ApiCall {
  protected readonly defaultHeaders: Record<string, string>;
  protected readonly timeoutMs: number;
  private abortControllers: Map<string, AbortController> = new Map();

  constructor(options: ApiCallOptions = {}) {
    this.defaultHeaders = options.headers || { 'Content-Type': 'application/json' };
    this.timeoutMs = options.timeoutMs || 30000;
  }

  private buildUrlWithParams(path: string, params?: Record<string, unknown> | object): string {
    let url = `${path.startsWith('/') ? path : '/' + path}`;
    if (params && Object.keys(params).length > 0) {
      const query = qs.stringify(params as qs.ParsedUrlQueryInput);
      url += (url.includes('?') ? '&' : '?') + query;
    }
    return url;
  }

  // GET
  protected async get<T = unknown>(
    path: string,
    params?: Record<string, unknown> | object,
    shouldAbortPrevious?: boolean,
    headers?: Record<string, string>,
  ): Promise<T> {
    const url = this.buildUrlWithParams(path, params);
    return this._fetch<T>('GET', url, undefined, headers, { shouldAbortPrevious });
  }

  // DELETE
  protected async delete<T = unknown>(
    path: string,
    params?: Record<string, unknown> | object,
    shouldAbortPrevious?: boolean,
    headers?: Record<string, string>,
  ): Promise<T> {
    const url = this.buildUrlWithParams(path, params);
    return this._fetch<T>('DELETE', url, undefined, headers, { shouldAbortPrevious });
  }

  // POST
  protected async post<T = unknown>(
    path: string,
    body?: Record<string, unknown> | object,
    shouldAbortPrevious?: boolean,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this._fetch<T>('POST', path, body ? JSON.stringify(body) : undefined, headers, {
      shouldAbortPrevious,
    });
  }

  // PUT
  protected async put<T = unknown>(
    path: string,
    body?: Record<string, unknown> | object,
    shouldAbortPrevious?: boolean,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this._fetch<T>('PUT', path, body ? JSON.stringify(body) : undefined, headers, {
      shouldAbortPrevious,
    });
  }

  // PATCH
  protected async patch<T = unknown>(
    path: string,
    body?: Record<string, unknown> | object,
    shouldAbortPrevious?: boolean,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this._fetch<T>('PATCH', path, body ? JSON.stringify(body) : undefined, headers, {
      shouldAbortPrevious,
    });
  }

  // 공통 fetch
  private async _fetch<T>(
    method: string,
    url: string,
    body?: string,
    headers?: Record<string, string>,
    options?: FetchOptions,
  ): Promise<T> {
    const key = `${method}:${url}`;
    if (options?.shouldAbortPrevious) {
      const prevController = this.abortControllers.get(key);
      if (prevController) {
        prevController.abort();
      }
    }
    const controller = new AbortController();
    this.abortControllers.set(key, controller);
    let timeoutTriggered = false;
    const timeout = setTimeout(() => {
      timeoutTriggered = true;
      controller.abort();
    }, this.timeoutMs);
    try {
      const response = await fetch(url, {
        method,
        headers: { ...this.defaultHeaders, ...headers },
        body,
        signal: controller.signal,
      });
      clearTimeout(timeout);
      this.abortControllers.delete(key);
      if (!response.ok) {
        const text = await response.text();
        throw new ApiRequestError(`API call failed: ${response.status} ${response.statusText} - ${text}`, {
          status: response.status,
          statusText: response.statusText,
          url,
          method,
          response: text,
        });
      }
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return response.json() as Promise<T>;
      }
      return (await response.text()) as unknown as T;
    } catch (err: any) {
      clearTimeout(timeout);
      this.abortControllers.delete(key);
      // 취소된 API 에 대한 응답 처리
      if (err?.name === 'AbortError') {
        if (timeoutTriggered) {
          throw new ApiTimeoutError('API request timed out', { url, method });
        } else {
          throw new ApiAbortError('API request was aborted by a new request', { url, method });
        }
      }
      if (
        err instanceof ApiRequestError ||
        err instanceof ApiTimeoutError ||
        err instanceof ApiNetworkError
      ) {
        throw err;
      }
      // 네트워크 에러 등 기타 fetch 에러
      throw new ApiNetworkError(err?.message || 'API network error', { url, method, error: err });
    }
  }
}
